/**
 * The static teaching metadata for the journey spine (P274).
 *
 * Extracted from JourneyView so the ops-res home can filter the same
 * ladder by owner without keeping a second copy of it -- two copies of
 * a catalogue is the drift this codebase keeps paying for.
 *
 * The backend returns the DYNAMIC signal (status, counts, blockers);
 * this is the part that does not change with the estate.
 */
export type Owner =
  | "admin"
  | "steward"
  | "opsres"
  | "cro"
  | "reviewer"
  | "compliance"
  | "ops"
  | "approver"
  /* ROUND 13 H, thirteenth round -- the spine had no station owned by
     the Service Owner. Every one belonged to somebody else, and the
     persona the tolerance ceremony BEGINS with could open the ladder
     and find nothing of his on it. His work was there, collapsed into
     the CRO's line: declaring a tolerance is not signing it. */
  | "service_owner";

export interface StepMeta {
  route: string;
  owner: Owner;
  /**
   * F2 -- the step title names a construct ("Topology edges (IBS to
   * service)", "IBSes admitted", "Impact tolerances"). The Journey is
   * where several personas met these for the first time, and it said
   * only whether the count was done.
   *
   * Optional: a step whose title is already plain English gets no
   * tip, because a marker on a self-explanatory line is noise.
   */
  termKey?: string;
  /** Round 16 N1 -- a station whose seats sign in turn names them in
   *  turn ("CRO, then CIO") instead of a single owner label. */
  ownerLabelKey?: string;
}

// Keyed by the backend step key. Route = where you go to do/see it.
export const STEP_META: Record<string, StepMeta> = {
  bank_profile: { route: "/admin/bank-setup", owner: "admin" },
  connectors: { route: "/admin/connectors", owner: "admin", termKey: "connector" },
  services: { route: "/foundation/admin/services", owner: "steward" },
  capabilities: { route: "/foundation/admin/capabilities", owner: "steward" },
  ibs_service_links: {
    route: "/admin/data-import",
    owner: "steward",
    termKey: "architecture_edges",
  },
  journeys: { route: "/twin/journeys", owner: "steward" },
  data_baseline: {
    route: "/admin/data-baseline",
    owner: "steward",
    termKey: "data_baseline",
  },
  ibs_register: { route: "/foundation/admin/ibses", owner: "opsres", termKey: "ibs" },
  // E3: the admission ceremony is four stations, not one line. Each
  // is done in the same room but by a different hand, and the
  // collapsed version could not say which hand the work was waiting
  // on.
  ibs_derive: { route: "/twin/ibs-admission", owner: "opsres" },
  ibs_challenge: { route: "/twin/ibs-admission", owner: "reviewer" },
  ibs_admission: { route: "/twin/ibs-admission", owner: "cro", termKey: "admission" },
  // P429.6 -- after IBS admission, the paper's adoption order. Route 2:
  // the CRO leads, the CIO signs beside.
  cif_register: {
    route: "/twin/cifs",
    owner: "cro",
    ownerLabelKey: "modules.journey.ownerCroThenCio",
  },
  mvb_score: { route: "/twin/mvb-candidates", owner: "opsres", termKey: "mvb" },
  mvb_declared: { route: "/twin/mvb-candidates", owner: "opsres", termKey: "mvb" },
  mvb_signoff: { route: "/foundation/mvb", owner: "cro", termKey: "dual_signoff" },
  tolerance_declared: {
    route: "/twin/ibs-portfolio",
    owner: "service_owner",
    termKey: "impact_tolerance",
  },
  impact_tolerances: {
    // A3 -- was the admin IBS register: a list, admin-gated, with no
    // tolerance on it and no form. The dimensions page names every IBS
    // that has no tolerance and links each to where it is declared.
    route: "/twin/tolerance-metrics",
    owner: "cro",
    termKey: "impact_tolerance",
  },
  risk_appetite: {
    route: "/foundation/admin/risk-appetite-statements",
    owner: "cro",
  },
  obligation_map: { route: "/framework", owner: "compliance" },
  assessment_cycle: {
    route: "/framework/assessments",
    owner: "compliance",
    termKey: "assessment_cycle",
  },
  caps: { route: "/framework", owner: "compliance" },
  telemetry: { route: "/twin/telemetry", owner: "ops" },
  resilience: { route: "/twin/cascade", owner: "ops" },
  open_findings: { route: "/twin/cascade", owner: "ops", termKey: "finding" },
  /* ROUND 15 H6 -- where an incident is raised. The war room judges it
     against tolerance and the incident report drafts itself from it. */
  incidents: { route: "/twin/findings/new", owner: "opsres", termKey: "finding" },
  evidence_out: { route: "/board", owner: "approver" },
};
